/**
 * The app is installed and cached, so a new version is picked up only when the person says so:
 * a forced reload could throw away a form that is half filled in or a queue that has not been sent.
 */

import { useSyncExternalStore } from "react";
import { isNativeApp } from "../platform/native";

let update: (() => void) | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** The service worker found a new version: show the button. */
export function offerUpdate(apply: () => void): void {
  update = apply;
  notify();
}

export const applyUpdate = (): void => update?.();

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(subscribe, () => update !== null, () => false);
}

/** Register the service worker (not in development, and only where the browser has one). */
export async function registerServiceWorker(): Promise<void> {
  if (isNativeApp() || typeof navigator === "undefined" || !("serviceWorker" in navigator) || import.meta.env.DEV) return;
  try {
    const { registerSW } = await import("virtual:pwa-register");
    const updateSW = registerSW({ onNeedRefresh: () => offerUpdate(() => void updateSW(true)) });
  } catch {
    /* the app works without the service worker, only not offline */
  }
}

/** For tests. */
export function resetUpdateForTests(): void {
  update = null;
  notify();
}
